import clsx from "clsx";
import type { Op, SwitchState } from "../../builderModel";

interface OperatorSwitchProps {
  /** What it joins, for screen readers: "How keywords combine with tickers and tags". */
  label: string;
  state: SwitchState;
  /** What's set, which the switch shows while it's working. */
  value: Op;
  onChange?: (next: Op) => void;
  /** Read-only, as in the concierge preview. */
  readOnly?: boolean;
}

/**
 * The or | and between two boxes, on the spine.
 *
 * Only a switch whose sides both hold something is offered. One an included
 * list keeps at "or" shows a plain "or" saying why on hover; one with an
 * empty side shows a plain "or", since that's all it can mean.
 */
export default function OperatorSwitch({
  label,
  state,
  value,
  onChange,
  readOnly,
}: OperatorSwitchProps) {
  if (!state.shown || readOnly) {
    const shown = state.shown ? value : "OR";
    return (
      <span
        title={
          state.heldBy
            ? `Stays “or” while ${state.heldBy} is included, so it matches the way it does on its own.`
            : undefined
        }
        className={clsx(
          "px-2 text-xs",
          readOnly && state.shown ? "font-semibold text-gray-300" : "text-gray-500",
          state.heldBy && "cursor-help underline decoration-dotted decoration-gray-600 underline-offset-4"
        )}
      >
        {shown === "AND" ? "and" : "or"}
      </span>
    );
  }

  const segment = (op: Op, text: string) => {
    const on = value === op;
    return (
      <button
        type="button"
        aria-pressed={on}
        onClick={() => onChange?.(op)}
        className={clsx(
          "h-6 rounded border-0 px-2.5 py-0 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-squawk-blue/70",
          on
            ? "bg-squawk-blue/20 text-squawk-blue"
            : "bg-transparent text-gray-400 hover:text-gray-200"
        )}
      >
        {text}
      </button>
    );
  };

  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex gap-0.5 rounded-md border border-gray-700 bg-gray-900 p-0.5"
    >
      {segment("OR", "or")}
      {segment("AND", "and")}
    </div>
  );
}
